import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { 
  FolderKanban, 
  DollarSign, 
  Package, 
  HardHat,
  Users,
  TrendingUp,
  AlertCircle,
  CheckCircle2,
  Loader2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ERPDashboard = () => {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/erp/stats`, {
        headers: getAuthHeader()
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }
  
  // Stats with real data
  const statCards = [
    {
      title: 'Active Projects',
      value: stats?.projects?.active || 0,
      icon: FolderKanban,
      color: 'bg-blue-500',
      link: '/erp/projects'
    },
    {
      title: 'Total Revenue',
      value: `₹${((stats?.revenue?.total || 0) / 10000000).toFixed(1)}Cr`,
      icon: DollarSign,
      color: 'bg-green-500',
      link: '/erp/finance'
    },
    {
      title: 'Total Projects',
      value: stats?.projects?.total || 0,
      icon: FolderKanban,
      color: 'bg-purple-500',
      link: '/erp/projects'
    },
    {
      title: 'Completed Projects',
      value: stats?.projects?.completed || 0,
      icon: CheckCircle2,
      color: 'bg-teal-500',
      link: '/erp/projects'
    },
  ];

  const quickActions = [
    { label: 'Create New Project', path: '/erp/projects', color: 'bg-primary' },
    { label: 'Generate Invoice', path: '/erp/finance', color: 'bg-green-600' },
    { label: 'Add Purchase Order', path: '/erp/procurement', color: 'bg-purple-600' },
    { label: 'Site Progress Report', path: '/erp/site-management', color: 'bg-orange-600' },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Welcome Banner */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Welcome to Construction ERP
        </h1>
        <p className="text-gray-600">
          Comprehensive enterprise resource planning system for Florentina Infra LLP
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {statCards.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <Card 
              key={index}
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(stat.link)}
            >
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">{stat.title}</p>
                    <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                  <div className={`${stat.color} p-4 rounded-lg`}>
                    <Icon className="text-white" size={28} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {quickActions.map((action, index) => (
              <button
                key={index}
                onClick={() => navigate(action.path)}
                className={`${action.color} text-white p-4 rounded-lg hover:opacity-90 
                           transition-opacity font-medium text-center`}
              >
                {action.label}
              </button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Activity & Alerts */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <CheckCircle2 className="mr-2 text-green-500" size={20} />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-gray-500">
              <p>No recent activity</p>
              <p className="text-sm mt-2">Activity will appear here as you use the system</p>
            </div>
          </CardContent>
        </Card>

        {/* Alerts */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <AlertCircle className="mr-2 text-orange-500" size={20} />
              Alerts & Notifications
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-gray-500">
              <p>No alerts</p>
              <p className="text-sm mt-2">Important notifications will appear here</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Getting Started Guide */}
      <Card className="mt-6 bg-gradient-to-r from-primary/5 to-secondary/5 border-primary/20">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            🚀 Getting Started with ERP System
          </h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Module Overview:</h4>
              <ul className="space-y-1 text-gray-600">
                <li>• <strong>Projects:</strong> Manage construction projects end-to-end</li>
                <li>• <strong>Finance:</strong> Budgeting, invoicing, and expense tracking</li>
                <li>• <strong>Procurement:</strong> Material orders and vendor management</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Additional Modules:</h4>
              <ul className="space-y-1 text-gray-600">
                <li>• <strong>Site Management:</strong> Daily progress and issue tracking</li>
                <li>• <strong>CRM:</strong> Client and lead management</li>
                <li>• <strong>Reports:</strong> Analytics and business intelligence</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ERPDashboard;